import {
  Button,
  Chip,
  Skeleton,
  styled,
  TableSortLabel,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useState, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DSNSummaryModal from 'src/components/dailySessionNotes/DSN summary/DSNSummaryModal';
import { getDSNNoteSummary, resetDsnSummary } from 'src/redux/actions/therapist-dashboard-action';
import { CheckIcon, IconTask, TimeIcon } from '../dashboard-icon';
import { FiberManualRecord } from '@mui/icons-material';
import moment from 'moment';
import useCurrentUser from 'src/hooks/use-current-user';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { toCamelCase } from 'src/pages/schedule/therapist/UI-Improvment/therapist-task-list';
import { appointmentBookFlag } from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
  />
))(({ }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    color: 'rgba(0, 0, 0, 0.87)',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: ' rgba(0, 0, 0, 0.24) 0px 3px 8px',
    fontSize: 11,
  },
}));

interface PatientListTableProps {
  items?: any;
  handleOpenDrawer: any;
  handleSortRequest: any;
  orderBy: any;
  order: any;
  handleAppointmentSortRequest?: any;
  isAppointmentSortActive?: boolean;
  appointmentOrder?: string;
  selectedDate: any
}

const ListingTable: FC<PatientListTableProps> = (props) => {
  const dispatch = useDispatch()
  const { items = [], handleOpenDrawer, handleSortRequest, orderBy, order, handleAppointmentSortRequest, isAppointmentSortActive = false, appointmentOrder = 'asc', selectedDate } = props;
  const [discipline] = useCurrentUser([
    'discipline',
  ]);

  const getDashboardPatientList = useSelector((state: any) => state.getDashboardPatientReducer);
  const dsnSummary = useSelector((state: any) => state.getDsnSummaryReducer)
  const [ isAppointmentBook ] = useFeatureToggleWhenReady(appointmentBookFlag, 'FACILITY');
  const statusStyleProps: any = {
    IN_PROGRESS: {
      marginLeft: '10px',
      color: '#6366F1',
      backgroundColor: '#EBEEFE',
    },
    NOT_STARTED: {
      marginLeft: '10px',
      color: '#6C737F',
      backgroundColor: 'rgba(17, 25, 39, 0.08)',
    },
    FINALIZED: {
      marginLeft: '10px',
      color: '#107569',
      backgroundColor: 'rgba(21, 183, 158, 0.12)',
    },
    SIGNED: {
      marginLeft: '10px',
      color: '#107569',
      backgroundColor: 'rgba(21, 183, 158, 0.12)',
    },
  };

  const [openSummary, setOpenSummary] = useState(false);

  const handleOpenDsnSummary = (patient: any) => {
    dispatch(resetDsnSummary())
    dispatch(getDSNNoteSummary({
      patientId: patient?.uuid,
      type: 'dashboard',
      params: {
        discipline: discipline,
        localDate: selectedDate,
      }
    }))
    setOpenSummary(true)
  }

  const getInitialsFromName = (name: string): string => {
    if (!name) return '';
    
    const nameWithoutParentheses = name.replace(/\s*\([^)]*\)\s*/g, '').trim();
    
    const words = nameWithoutParentheses.split(/\s+/);
    if (words.length === 0) return '';
    
    const firstInitial = words[0]?.charAt(0)?.toUpperCase() || '';
    const secondInitial = words[1]?.charAt(0)?.toUpperCase() || '';
    
    return firstInitial + secondInitial;
  }


  return (
    <Box sx={{ position: 'relative' }}>
      <Table sx={{ minWidth: 700, tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '28%' }} />
          <col style={{ width: '22%' }} />
          <col style={{ width: '18%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '16%' }} />
        </colgroup>
        <TableHead
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: '#fff',
            '& .MuiTableCell-root': { backgroundColor: '#fff' },
          }}
        >
          <TableRow>
            <TableCell sortDirection={orderBy === 'patientName' ? order : false}>
              <TableSortLabel
                active={orderBy === 'patientName'}
                direction={order === 'desc' ? 'desc' : 'asc'}
                onClick={handleSortRequest}
              >
                Patient Name
              </TableSortLabel>
            </TableCell>
            <TableCell>Task</TableCell>
            <TableCell sortDirection={isAppointmentSortActive ? (appointmentOrder === 'desc' ? 'desc' : 'asc') : false}>
              {(handleAppointmentSortRequest && isAppointmentBook) ? (
                <TableSortLabel
                  active={isAppointmentSortActive}
                  direction={appointmentOrder === 'desc' ? 'desc' : 'asc'}
                  onClick={handleAppointmentSortRequest}
                >
                  Appointment
                </TableSortLabel>
              ) : (
                isAppointmentBook ? 'Appointment' : 'Duration'
              )}
            </TableCell>
            <TableCell>Status</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {getDashboardPatientList?.loading ? (
            Array.from({ length: 6 }).map((_, rowIndex) => (
              <TableRow key={`skeleton-${rowIndex}`}>
                <TableCell>
                  <Stack alignItems="center" direction="row" gap="1rem">
                    <Skeleton animation="wave" variant="circular" width={42} height={42} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton animation="wave" width="70%" />
                      <Skeleton animation="wave" width="40%" />
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell><Skeleton animation="wave" width="60%" /></TableCell>
                <TableCell><Skeleton animation="wave" width="60%" /></TableCell>
                <TableCell><Skeleton animation="wave" width="60%" /></TableCell>
                <TableCell align="right" sx={{ maxWidth: '8rem', whiteSpace: 'nowrap' }}>
                  <Skeleton animation="wave" width="80%" sx={{ ml: 'auto' }} />
                </TableCell>
              </TableRow>
            ))
          ) : !!items.length ? (
            items?.map((patient: any, index: number) => {
              const nextItem = items?.[index + 1];
              const isLastTaskOfPatient =
                !nextItem ||
                (nextItem?._patientUuid || nextItem?.uuid) !==
                  (patient?._patientUuid || patient?.uuid);
              return (
                <TableRow
                  hover
                  key={`${patient?.uuid}-${patient?.therapyTypes?.join('_') ?? ''}-${index}`}
                  sx={{
                    cursor: 'pointer',
                    '& > td': isLastTaskOfPatient ? {} : { borderBottom: 'none' },
                  }}
                >
                  <TableCell>
                    {patient?._showPatientHeader === false ? null : (
                      <Stack
                        alignItems="center"
                        direction="row"
                        gap="1rem"
                      >
                        <Avatar
                          src={`data:image/png;base64,${patient?.profileImage}`}
                          sx={{
                            height: 42,
                            width: 42,
                          }}
                        >
                          {patient?.lastName && patient?.firstName
                            ? `${patient?.lastName?.toUpperCase()?.at(0)}${patient?.firstName?.toUpperCase()?.at(0)}`
                            : patient?.name
                            ? getInitialsFromName(patient.name)
                            : ''}
                        </Avatar>
                        <Box>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="subtitle2">
                              {patient?.lastName
                                ? `${patient?.lastName}, ${patient?.firstName}`
                                : patient?.name}
                            </Typography>
                          </Stack>
                          <Typography
                            sx={{ color: '#6C737F' }}
                            variant="caption"
                          >
                            {patient?.patient_term}
                          </Typography>
                        </Box>
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      textTransform: 'capitalize',
                    }}
                  >
                    { patient?.scheduleItemType === "APPOINTMENT" && isAppointmentBook
                      ? <Box display={'flex'} gap={1} alignItems={'center'} textAlign={'center'} ml={3}>
                          <EventAvailableIcon />
                          <Typography variant="caption3Text">{toCamelCase(patient?.scheduleItemType)}</Typography>
                      </Box>

                      : <Stack
                        direction={'row'}
                        gap={1}
                        alignItems={'center'}
                        textAlign={'center'}
                      >
                        <Box sx={{ width: '16px' }}>
                          {patient?.notes ? (
                            <FiberManualRecord
                              sx={{ fontSize: '12px', color: '#9747FF' }}
                              color="primary"
                            />
                          ) : null}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          {patient?.taskNameWithId?.map((taskName: any) => (
                            <span title={taskName?.value}>
                              <IconTask iconType={taskName.key} />
                              {/* <Typography sx={{ fontSize: '14px' }}>{taskName?.value}</Typography> */}
                            </span>
                          ))}
                          {patient?.taskNameWithId?.length === 1 ? (
                            <Typography variant="body1">
                              {patient?.taskNameWithId[0]?.value}
                            </Typography>
                          ) : (
                            patient?.taskNameWithId?.length > 1 && (
                              <LightTooltip
                                title={patient?.taskNameWithId?.map((group: any) => (
                                  <Typography variant="caption3Text">{group?.value}</Typography>
                                ))}
                              >
                                <Typography variant="body1">
                                  {patient?.taskNameWithId[0]?.value}+1
                                </Typography>
                              </LightTooltip>
                            )
                          )}

                          <Box>
                            {patient?.therapyNames?.map((group: any) => {
                              return (
                                <Typography
                                  sx={{
                                    ml: 1,
                                    color:
                                      group?.charAt(0) === 'G'
                                        ? '#06AED4'
                                        : group?.charAt(0) === 'C'
                                        ? '#F79009'
                                        : null,
                                  }}
                                  variant="caption"
                                  noWrap
                                  key={group}
                                >
                                  {group}
                                </Typography>
                              );
                            })}
                          </Box>
                        </Box>
                      </Stack>}
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TimeIcon />
                      <Typography
                        sx={{ marginLeft: '5px' }}
                        variant="body2"
                      >
                        {patient?.appointmentStartTime
                          ? (() => {
                              const timeMoment = moment.utc(patient?.appointmentStartTime, moment.ISO_8601);
                              const minutes = timeMoment.minutes();
                              const formattedTime = minutes === 0
                                ? `${timeMoment.format('h')} ${timeMoment.format('A')}`
                                : `${timeMoment.format('h')}.${timeMoment.format('mm')} ${timeMoment.format('A')}`;
                              return `${formattedTime}; ${patient?.taskMinutes} Min.`;
                            })()
                          : patient?.taskMinutes}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {patient?.isFutureAdmission ? (
                      <Chip
                        label={
                          <Typography variant="overline" sx={{ textTransform: 'none' }}>
                            NEW ADMISSION
                          </Typography>
                        }
                        variant="outlined"
                        sx={statusStyleProps['NOT_STARTED']}
                      />
                    ) : patient?.status !== 'SIGNED' ? (
                      <Chip
                        label={
                          <Typography variant="overline">
                            {patient?.status === 'NOT_STARTED' || !patient?.status
                              ? 'Incomplete'
                              : patient?.status
                                  ?.split('_')
                                  .map(
                                    (item: string) =>
                                      item.slice(0, 1) + item.toLowerCase().slice(1, item.length)
                                  )
                                  .join(' ')}
                          </Typography>
                        }
                        variant="outlined"
                        sx={statusStyleProps[patient?.status ? patient?.status : 'NOT_STARTED']}
                      />
                    ) : (
                      <Chip
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <CheckIcon />
                            <Typography variant="overline">Signed</Typography>
                          </Box>
                        }
                        variant="outlined"
                        sx={{
                          marginLeft: '10px',
                          color: '#107569',
                          backgroundColor: 'rgba(21, 183, 158, 0.12)',
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    align="right"

                    sx={{ maxWidth: '8rem', whiteSpace: "nowrap" }}
                  >
                    {!patient?.isFutureAdmission && patient?._showPatientHeader !== false && (
                      <>
                        <Button size='small' variant='text' onClick={() => handleOpenDsnSummary(patient)} >DSN Summary</Button>
                        <Button size='small' onClick={() => handleOpenDrawer(patient)} variant='text' >POC</Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={5}
                align="center"
              >
                <Typography
                  variant="h6"
                  sx={{ margin: '2.5rem auto' }}
                >
                  No Records Found
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <DSNSummaryModal
        open={openSummary}
        onClose={() => setOpenSummary(false)}
        data={dsnSummary?.response}
      />

    </Box>
  );
};

export default ListingTable;